import { useQuery, useMutation } from "@apollo/client/react";
import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RenderInputByTypeMobile } from "../../../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { useVisita } from "../../../../../../context/VisitaContext";
import SAVE_PROGRAMA_HE, { adaptAddVisitaHeInput } from "../../../../../../data/graphQL/mutations/Visitas/saveProgramaHe";
import { useStepSave } from "../../../../hooks/useStepSave";
import CardBox from "../../CardBox/CardBox";
import { filterVisitaData, getInputNames } from "../../../../utils/formDataHelper";
import { getStringQueryCombosRI } from "../../../../../../data/graphQL/queries/ResumenIngreso/getAllCombosRI";
import { adaptCombosRIArray } from "../../../../../../adapters/resumenIngreso/combosRIAdapter";
import { handleChangeImageVisita } from "../../../../utils/handleChangeImageVisita";
import { useDeleteArchivoVisita } from "../../../../hooks/useDeleteArchivoVisita";
import { tipoArchivoVisita } from "../../../../constants/TipoArchivosVisita";
import { validateStepFields } from "../../../../utils/visitaStepValidation";

export default function ProgramaHEMedico({ readOnly, onSaveRef, onExposeFormatted, complicacionesCombo, esUrgencia }) {
  const [formData, setFormData] = useState({});
  const [combosRI, setCombosRI] = useState();

  const { visita, setVisita, saveArchivoVisita } = useVisita();
  const { deleteArchivo } = useDeleteArchivoVisita();

  const inputCombos = inputs.filter((i) => !!i.comboName);

  const { data: dataCombosRI } = useQuery(getStringQueryCombosRI(inputCombos), {
    fetchPolicy: "no-cache"
  });

  useEffect(() => {
    if (!dataCombosRI) return;
    setCombosRI(adaptCombosRIArray(dataCombosRI))
  }, [dataCombosRI]);

  const [save] = useMutation(SAVE_PROGRAMA_HE);

  const formattedInputs = useMemo(() => {
    return inputs.map((input) => {
      const newInput = { ...input };
      if (newInput.comboName) newInput.options = combosRI?.[input.name];

      if (input.name === "complicaciones") {
        const ningunaId = complicacionesCombo?.find((c) => c.displayName.toLowerCase() === 'ninguna')?.id;
        newInput.options = complicacionesCombo?.map((opt) => {
          if (Array.isArray(formData.complicaciones)) {
            const hasNinguna = formData.complicaciones.includes(ningunaId);
            const hasOther = formData.complicaciones.some((id) => id !== ningunaId);
            if (hasNinguna) {
              // Si "ninguna" está seleccionada, deshabilitar todas menos "ninguna"
              return { ...opt, disabled: opt.id !== ningunaId };
            }
            if (hasOther) {
              // Si hay alguna distinta de "ninguna", deshabilitar "ninguna"
              return { ...opt, disabled: opt.id === ningunaId };
            }
          }
          return { ...opt, disabled: false };
        });
      }

      if (readOnly) newInput.disabled = true;
      if (esUrgencia) newInput.required = false;

      return newInput;
    });
  }, [readOnly, combosRI, complicacionesCombo, formData?.complicaciones, esUrgencia]);

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeFile = useCallback((name, fileValue) => {
    handleChangeImageVisita({
      saveArchivoVisita,
      name,
      fileValue,
      idVisita: visita.idVisita,
      handleChange,
      idTipoArchivo: tipoArchivoVisita.LABORATORIO
    });
  }, [visita.idVisita, saveArchivoVisita]);

  const onDeleteImage = (name) => {
    const file = formData[name];
    deleteArchivo({ idArchivo: file.idArchivo, name, handleChange });
  };

  useStepSave(onSaveRef, async (validate) => {
    if (validate) validateStepFields({ formattedInputs, formData });
    const { data } = await save({ variables: { input: adaptAddVisitaHeInput(formData) } });

    const idVisitaHe = data?.saveVisitaHe?.idVisitaHe;
    if (!idVisitaHe) throw new Error("Error al guardar");

    setVisita((prev) => ({ ...prev, ...formData, idVisitaHe }));
  });

  useEffect(() => {
    if (visita) {
      const inputNames = getInputNames(inputs);

      setFormData({
        ...filterVisitaData(visita, inputNames),
        idVisitaHe: visita.idVisitaHe,
        idVisita: visita.idVisita
      });
    }
  }, [visita]);

  useEffect(() => {
    onExposeFormatted?.({
      stepId: "programaHECateter",
      stepName: "HE - Cuidado del catéter",
      formattedInputs
    });
  }, [onExposeFormatted, formattedInputs]);

  return (
    <>
      <CardBox>
        <Grid container spacing={2}>
          {formattedInputs.
            filter((input) => !input.hidden).
            map((input) =>
              <Grid size={{ xs: 12 }} key={input.name}>
                <RenderInputByTypeMobile
                  input={input}
                  value={formData[input.name]}
                  handleChange={input.type === "file" ? handleChangeFile : handleChange}
                  handleDelete={input.type === "file" ? onDeleteImage : null}
                  row={input.type === "multiple_boolean"} />

              </Grid>
            )}
        </Grid>
      </CardBox>
    </>);

}

const inputs = [
  {
    name: "idComboRIValorSellado",
    displayName: "Sellado de catéter",
    comboName: "Sellado de catéter AP",
    type: "select",
    required: true
  },
  {
    name: "indicacionComentario",
    displayName: "Comentarios de indicación",
    type: "long_string",
    maxLength: 4000
  },
  {
    name: "laboratorioAdjunto",
    displayName: "Laboratorio (adjuntar)",
    type: "file"
  },
  {
    name: "complicaciones",
    displayName: "Complicaciones",
    type: "multiple_select",
    required: true
  }];